import { Stack, Typography } from "@mui/material";
import { SkillAudience, SkillScope } from "generated-graphql/graphql";
import { Control, Controller } from "react-hook-form";

import type { SkillEditorFormValues } from "./SkillEditor";

import {
  SKILL_AUDIENCE_OPTIONS,
  SKILL_AUDIENCE_TOKENS,
  SKILL_SCOPE_OPTIONS,
  SKILL_SCOPE_TOKENS,
} from "../SkillAccess/skillAccessTokens";
import { SkillChipSelect } from "./SkillChipSelect";

type SkillAccessChipsProps = {
  control: Control<SkillEditorFormValues>;
  readOnly: boolean;
  /** When false, scope cannot be changed. */
  scopeEditable: boolean;
  /** Staff may create GLOBAL skills and move scope to or from GLOBAL. */
  allowGlobal?: boolean;
  /** Staff can mark a skill staff-only. Customers choose only-me or the organization. */
  allowStaffAudience?: boolean;
};

/** Who can use the skill and where it activates, as dropdown chips for the dialog title bar. */
export function SkillAccessChips({
  control,
  readOnly,
  scopeEditable,
  allowGlobal = false,
  allowStaffAudience = false,
}: SkillAccessChipsProps) {
  const scopeDisabled = readOnly || !scopeEditable;
  const scopeOptions =
    scopeDisabled || allowGlobal
      ? SKILL_SCOPE_OPTIONS
      : SKILL_SCOPE_OPTIONS.filter((option) => option.value !== SkillScope.Global);
  const audienceOptions =
    readOnly || allowStaffAudience
      ? SKILL_AUDIENCE_OPTIONS
      : SKILL_AUDIENCE_OPTIONS.filter((option) => option.value !== SkillAudience.Staff);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2" color="text.secondary" component="span">
        Usable by
      </Typography>
      <Controller
        name="audience"
        control={control}
        render={({ field }) => (
          <SkillChipSelect
            label="Who can use it"
            value={SKILL_AUDIENCE_TOKENS[field.value]}
            options={audienceOptions}
            onChange={field.onChange}
            disabled={readOnly}
          />
        )}
      />
      <Typography variant="body2" color="text.secondary" component="span">
        in
      </Typography>
      <Controller
        name="scope"
        control={control}
        render={({ field }) => (
          <SkillChipSelect
            label="Where it activates"
            value={SKILL_SCOPE_TOKENS[field.value]}
            options={scopeOptions}
            onChange={field.onChange}
            disabled={scopeDisabled}
          />
        )}
      />
    </Stack>
  );
}
